import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { employeesAPI } from '../../services/api';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import Pagination from '../../components/Common/Pagination';
import { fmtDate } from '../../utils/dateUtils';

const CAT_LABEL = {
  OPT: 'OPT', STEM_OPT: 'STEM OPT', CPT: 'CPT',
  H1B: 'H-1B', H4_EAD: 'H-4 EAD', GC: 'Green Card', CITIZENSHIP: 'Citizenship',
};
const CAT_COLOR = {
  OPT:         'bg-blue-100 text-blue-700',
  STEM_OPT:    'bg-violet-100 text-violet-700',
  CPT:         'bg-green-100 text-green-700',
  H1B:         'bg-amber-100 text-amber-700',
  H4_EAD:      'bg-pink-100 text-pink-700',
  GC:          'bg-emerald-100 text-emerald-700',
  CITIZENSHIP: 'bg-teal-100 text-teal-700',
};

const SORT_OPTIONS = [
  { value: 'created_at|DESC', label: 'Date Added (Newest)' },
  { value: 'created_at|ASC',  label: 'Date Added (Oldest)' },
  { value: 'name|ASC',        label: 'Name (A → Z)' },
  { value: 'name|DESC',       label: 'Name (Z → A)' },
  { value: 'ead_expiry|ASC',  label: 'EAD Expiry (Soonest)' },
  { value: 'ead_expiry|DESC', label: 'EAD Expiry (Latest)' },
  { value: 'email|ASC',       label: 'Email (A → Z)' },
];

export default function EmployeeList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [employees,  setEmployees]  = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  const [search,   setSearch]   = useState(searchParams.get('search')   || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [sortKey,  setSortKey]  = useState(searchParams.get('sort')     || 'created_at|DESC');
  const [page,     setPage]     = useState(Number(searchParams.get('page')) || 1);

  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [sort, order] = sortKey.split('|');

  const fetchEmployees = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await employeesAPI.list({ search, category, page, limit: 10, sort, order });
      setEmployees(res.data.data.employees);
      setPagination(res.data.data.pagination);
    } catch {
      setError('Failed to load employees.');
    } finally {
      setLoading(false);
    }
  }, [search, category, page, sort, order]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  useEffect(() => {
    const p = {};
    if (search)              p.search   = search;
    if (category)            p.category = category;
    if (sortKey !== 'created_at|DESC') p.sort = sortKey;
    if (page > 1)            p.page     = String(page);
    setSearchParams(p, { replace: true });
  }, [search, category, sortKey, page, setSearchParams]);

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await employeesAPI.delete(deleteTarget.id);
      setDeleteTarget(null);
      fetchEmployees();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete employee.');
    } finally {
      setDeleteLoading(false);
    }
  }

  /* ── Sort header helper ──────────────────────────────────────────────────── */
  function SortTh({ col, label }) {
    const active = sort === col;
    const nextOrder = active && order === 'ASC' ? 'DESC' : 'ASC';
    return (
      <th
        onClick={() => { setSortKey(`${col}|${nextOrder}`); setPage(1); }}
        className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none whitespace-nowrap"
      >
        {label}
        <span className="ml-1 text-slate-300">
          {active ? (order === 'ASC' ? '↑' : '↓') : '↕'}
        </span>
      </th>
    );
  }

  return (
    <div className="space-y-5">

      {/* Toolbar */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <form onSubmit={(e) => { e.preventDefault(); setPage(1); fetchEmployees(); }}
            className="flex gap-2 flex-1 max-w-md">
            <input type="text" placeholder="Search name, email, phone…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <button type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
              Search
            </button>
          </form>

          <div className="flex gap-2 flex-wrap">
            <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
              <option value="">All Categories</option>
              <option value="OPT">OPT</option>
              <option value="STEM_OPT">STEM OPT</option>
              <option value="CPT">CPT</option>
              <option value="H1B">H-1B</option>
              <option value="H4_EAD">H-4 EAD</option>
              <option value="GC">Green Card</option>
              <option value="CITIZENSHIP">Citizenship</option>
            </select>
            <select value={sortKey} onChange={(e) => { setSortKey(e.target.value); setPage(1); }}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button onClick={() => navigate('/employees/create')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
              + Add Employee
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? <LoadingSpinner message="Loading employees…" />
        : error   ? <div className="p-8 text-center text-red-600">{error}</div>
        : employees.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-4xl mb-3">👤</p>
            <p className="text-slate-600 font-medium">No employees found</p>
            <p className="text-slate-400 text-sm mt-1">
              {search || category ? 'Try different search/filter.' : 'Add your first employee.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <SortTh col="name"       label="Name" />
                    <SortTh col="email"      label="Email" />
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                    <SortTh col="ead_expiry" label="EAD Expiry" />
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Docs</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {employees.map((emp) => (
                    <tr key={emp.id} className="hoverable group">
                      <td className="px-4 py-3 font-medium text-slate-800">
                        {emp.first_name} {emp.last_name}
                      </td>
                      <td className="px-4 py-3 text-slate-500">{emp.email}</td>
                      <td className="px-4 py-3 text-slate-500">
                        {emp.country_code && <span className="text-slate-400">{emp.country_code} </span>}
                        {emp.phone}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${CAT_COLOR[emp.category] || 'bg-slate-100 text-slate-600'}`}>
                          {CAT_LABEL[emp.category] || emp.category || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        <ExpiryCell date={emp.ead_expiry_date} />
                      </td>
                      <td className="px-4 py-3">
                        <DocsProgress uploaded={emp.uploaded_docs} total={emp.total_docs} pct={emp.completion_pct} />
                      </td>
                      <td className="px-4 py-3">
                        <ComplianceChip emp={emp} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => navigate(`/employees/${emp.id}`)}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium">View</button>
                          <button
                            onClick={() => setDeleteTarget({ id: emp.id, name: `${emp.first_name} ${emp.last_name}` })}
                            className="text-red-500 hover:text-red-700 text-xs font-medium">
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination && (
              <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-sm text-slate-500">
                <span>
                  Showing {(pagination.current_page-1)*pagination.per_page+1}–
                  {Math.min(pagination.current_page*pagination.per_page, pagination.total)} of {pagination.total}
                </span>
                <Pagination currentPage={pagination.current_page}
                  totalPages={pagination.total_pages}
                  onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="text-center mb-5">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🗑️</span>
              </div>
              <h3 className="font-bold text-slate-800 text-lg">Delete Employee?</h3>
              <p className="text-slate-500 text-sm mt-1">
                Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
                This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}
                className="flex-1 border border-slate-300 text-slate-700 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={confirmDelete} disabled={deleteLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
                {deleteLoading
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"/>Deleting…</>
                  : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExpiryCell({ date }) {
  if (!date) return <span className="text-slate-400">—</span>;
  const days = Math.floor((new Date(date) - new Date()) / 86400000);
  return (
    <span className={days < 0 ? 'text-red-600 font-medium' : days <= 30 ? 'text-orange-500 font-medium' : 'text-slate-600'}>
      {fmtDate(date)}
    </span>
  );
}

function DocsProgress({ uploaded, total, pct }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${pct===100?'bg-green-500':pct>=50?'bg-yellow-500':'bg-red-400'}`}
          style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-500">{uploaded}/{total}</span>
    </div>
  );
}

function ComplianceChip({ emp }) {
  if (emp.expired_docs  > 0)               return <Chip color="red"    label="⚠ Expired" />;
  if (emp.uploaded_docs < emp.total_docs)  return <Chip color="orange" label="Incomplete" />;
  if (emp.expiring_soon > 0)               return <Chip color="yellow" label="Expiring" />;
  return <Chip color="green" label="✓ Compliant" />;
}

function Chip({ color, label }) {
  const c = {
    red:    'bg-red-100 text-red-700',
    orange: 'bg-orange-100 text-orange-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    green:  'bg-green-100 text-green-700',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${c[color]}`}>
      {label}
    </span>
  );
}
